import { SkyDropdown, SkyInput } from "@skypointcloud/skypoint-ui";
import { ROLE_LABELS, ROLE_NAMES, type RoleName } from "@shared/permissions";
import { SearchIcon } from "@/lib/icons";
import type { AccessListFilters } from "@/hooks/use-access";

/**
 * Search, role and status over the user list (canvas 07, "Filters").
 *
 * Search and status are sent to the server; role is applied in the browser,
 * because a row here is a GRANT and no list route filters by the role on one.
 * Saying so in a comment rather than adding an unused query parameter keeps the
 * two halves honest.
 */
export function AccessFilters({
  filters,
  onChange,
  showStatus,
}: {
  filters: AccessListFilters;
  onChange: (next: AccessListFilters) => void;
  /** Only the platform surface takes a status filter; the others answer every status. */
  showStatus: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="access-filters">
      <div className="w-64">
        <SkyInput
          label="Search"
          type="search"
          placeholder="Search email or name"
          value={filters.search ?? ""}
          contentBefore={<SearchIcon />}
          testId="access-search"
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
        />
      </div>
      <div className="w-48">
        <SkyDropdown
          label="Role"
          testId="access-role-filter"
          selectedOptions={[filters.role ?? "all"]}
          options={[
            { value: "all", text: "All roles" },
            ...ROLE_NAMES.map((role) => ({ value: role, text: ROLE_LABELS[role] })),
          ]}
          onOptionSelect={(values) =>
            onChange({ ...filters, role: (values[0] ?? "all") as RoleName | "all" })
          }
        />
      </div>
      {showStatus ? (
        <div className="w-48">
          <SkyDropdown
            label="Status"
            testId="access-status-filter"
            selectedOptions={[filters.status ?? "all"]}
            options={[
              { value: "all", text: "All statuses" },
              { value: "pending", text: "Pending first sign-in" },
              { value: "active", text: "Active" },
              { value: "disabled", text: "Disabled" },
            ]}
            onOptionSelect={(values) =>
              onChange({
                ...filters,
                status: (values[0] ?? "all") as AccessListFilters["status"],
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}
